import { useRef, ReactNode } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { cn } from "@/libs/utils/tailwind/cn";
import ChevronIcon from "@/components/icons/ChevronIcon";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function Carousel({
  children,
  className,
  slidesPerView = 1,
  spaceBetween = 0,
  showArrows = true,
  showDots = true,
  noSafePadding = false,
  navigationPrevRef,
  navigationNextRef,
  paginationRef,
  onSlideChange,
  ...swiperProps
}: CarouselProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const paginationElRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType>();

  const prevButtonRef = navigationPrevRef || prevRef;
  const nextButtonRef = navigationNextRef || nextRef;
  const paginationButtonRef = paginationRef || paginationElRef;

  const shouldAddPadding = showArrows && !noSafePadding;

  return (
    <div
      className={cn(
        "relative",
        shouldAddPadding &&
          "[&_.swiper-slide]:px-10 [&_.swiper-slide]:sm:px-12",
        className
      )}
    >
      <Swiper
        modules={[Autoplay, EffectFade, Navigation, Pagination]}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        navigation={{
          prevEl: prevButtonRef.current,
          nextEl: nextButtonRef.current,
        }}
        pagination={{
          el: paginationButtonRef.current,
          clickable: true,
          bulletClass: "swiper-pagination-bullet",
          bulletActiveClass: "swiper-pagination-bullet-active",
        }}
        allowTouchMove
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          // Update navigation and pagination after swiper is initialized
          // Using requestAnimationFrame for better timing
          requestAnimationFrame(() => {
            const navigationParams = swiper.params.navigation;
            if (
              navigationParams &&
              typeof navigationParams === "object" &&
              prevButtonRef.current &&
              nextButtonRef.current
            ) {
              navigationParams.prevEl = prevButtonRef.current;
              navigationParams.nextEl = nextButtonRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }
            const paginationParams = swiper.params.pagination;
            if (
              paginationParams &&
              typeof paginationParams === "object" &&
              paginationButtonRef.current
            ) {
              paginationParams.el = paginationButtonRef.current;
              swiper.pagination.init();
              swiper.pagination.render();
              swiper.pagination.update();
            }
          });
        }}
        onSlideChange={(swiper) => {
          onSlideChange?.(swiper);
        }}
        {...swiperProps}
      >
        {children}
      </Swiper>

      {showArrows && (
        <>
          <button
            ref={prevButtonRef}
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-full p-2 transition-opacity hover:opacity-80 disabled:opacity-30"
            aria-label="Previous slide"
          >
            <ChevronIcon
              orientation="left"
              className="size-6 text-default-600"
            />
          </button>
          <button
            ref={nextButtonRef}
            className="absolute right-0 top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-full p-2 transition-opacity hover:opacity-80 disabled:opacity-30"
            aria-label="Next slide"
          >
            <ChevronIcon
              orientation="right"
              className="size-6 text-default-600"
            />
          </button>
        </>
      )}

      {showDots && (
        <div
          ref={paginationButtonRef}
          className="mt-6 flex justify-center gap-1.5 [&_.swiper-pagination-bullet]:size-3 [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:bg-default-100 [&_.swiper-pagination-bullet]:opacity-50 [&_.swiper-pagination-bullet-active]:!bg-primary [&_.swiper-pagination-bullet-active]:!opacity-100"
        />
      )}
    </div>
  );
}

interface CarouselProps {
  children: ReactNode;
  className?: string;
  slidesPerView?: number;
  spaceBetween?: number;
  showArrows?: boolean;
  showDots?: boolean;
  noSafePadding?: boolean;
  navigationPrevRef?: React.RefObject<HTMLButtonElement>;
  navigationNextRef?: React.RefObject<HTMLButtonElement>;
  paginationRef?: React.RefObject<HTMLDivElement>;
  onSlideChange?: (swiper: SwiperType) => void;
  [key: string]: unknown;
}

export { SwiperSlide };
